import {
  Component,
  Show,
  For,
  createSignal,
  createMemo,
  createEffect,
} from "solid-js";
import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";
import { Badge } from "../ui/Badge";
import { Card } from "../ui/Card";
import { Spinner } from "../ui/Spinner";
import { Dropdown, DropdownItem } from "../ui/Dropdown";
import { AiErrorBoundary } from "../ui/AiErrorBoundary";
import type {
  Proposal,
  ProposedActionId,
  ProposalStatus,
} from "../../api/types";
import { useToast } from "../../hooks";
import { groupActions, isQueryTool } from "./groupActions";
import { ProposalImpactSummary } from "./ProposalImpactSummary";
import { ActionGroupSection } from "./ActionGroupSection";
import { PartialApprovalWarning } from "./PartialApprovalWarning";

export interface ProposalReviewModalProps {
  proposal: Proposal | null | undefined;
  loading?: boolean;
  onClose: () => void;
  onApprove: (actionIds?: ProposedActionId[]) => Promise<void>;
  onReject: () => Promise<void>;
  onStartNewChat?: () => void;
}

const proposalStatusBadge: Record<
  ProposalStatus,
  { variant: string; label: string }
> = {
  pending: { variant: "todo", label: "Pending Review" },
  approved: { variant: "inProgress", label: "Approved" },
  executing: { variant: "inProgress", label: "Executing..." },
  executed: { variant: "done", label: "Executed" },
  partially_approved: { variant: "medium", label: "Partially Approved" },
  partially_executed: { variant: "high", label: "Partially Executed" },
  rejected: { variant: "cancelled", label: "Rejected" },
  expired: { variant: "none", label: "Expired" },
};

function mapProposalError(err: unknown): string {
  const msg = err instanceof Error ? err.message : String(err);
  const lower = msg.toLowerCase();
  if (lower.includes("expired")) {
    return "This proposal expired while you were reviewing it.";
  }
  if (
    lower.includes("conflict") ||
    lower.includes("invalid transition") ||
    lower.includes("already")
  ) {
    return "Another session already actioned this proposal.";
  }
  if (
    lower.includes("network") ||
    lower.includes("fetch") ||
    lower.includes("failed to fetch") ||
    lower.includes("load failed")
  ) {
    return "Could not reach server. Check your connection and try again.";
  }
  return "Failed to action proposal. Please try again.";
}

export const ProposalReviewModal: Component<ProposalReviewModalProps> = (
  props,
) => {
  const { showError, showSuccess } = useToast();

  const [selectedActions, setSelectedActions] = createSignal<
    Set<ProposedActionId>
  >(new Set());
  const [isExecuting, setIsExecuting] = createSignal(false);
  const [executionError, setExecutionError] = createSignal<string | null>(null);
  const [lastAction, setLastAction] = createSignal<
    (() => Promise<void>) | null
  >(null);

  createEffect(() => {
    const proposal = props.proposal;
    if (proposal) {
      const pendingIds = new Set(
        proposal.actions
          .filter((a) => a.status === "pending" && !isQueryTool(a.tool_name))
          .map((a) => a.id),
      );
      setSelectedActions(pendingIds);
    }
  });

  const visibleActions = createMemo(() => {
    const p = props.proposal;
    if (!p) return [];
    return p.actions.filter((a) => !isQueryTool(a.tool_name));
  });

  const pendingVisible = createMemo(() =>
    visibleActions().filter((a) => a.status === "pending"),
  );

  const groups = createMemo(() => {
    const p = props.proposal;
    if (!p) return [];
    return groupActions(p.actions).filter((g) => !g.isReadOnly);
  });

  const selectedCount = createMemo(() => selectedActions().size);
  const hasSelection = createMemo(() => selectedCount() > 0);

  const canTakeAction = createMemo(() => {
    const p = props.proposal;
    if (!p) return false;
    return p.status === "pending" || p.status === "partially_approved";
  });

  const toggleAction = (actionId: ProposedActionId) => {
    setSelectedActions((prev) => {
      const next = new Set(prev);
      if (next.has(actionId)) next.delete(actionId);
      else next.add(actionId);
      return next;
    });
  };

  const toggleGroup = (groupId: string) => {
    const group = groups().find((g) => g.id === groupId);
    if (!group) return;
    const pending = group.actions.filter((a) => a.status === "pending");
    const allSelected = pending.every((a) => selectedActions().has(a.id));
    setSelectedActions((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        pending.forEach((a) => next.delete(a.id));
      } else {
        pending.forEach((a) => next.add(a.id));
      }
      return next;
    });
  };

  const selectAll = () => {
    const p = props.proposal;
    if (!p) return;
    setSelectedActions(new Set(pendingVisible().map((a) => a.id)));
  };

  const deselectAll = () => {
    setSelectedActions(new Set<ProposedActionId>());
  };

  const handleApprove = async () => {
    const p = props.proposal;
    if (!p) return;
    setLastAction(() => handleApprove);
    setIsExecuting(true);
    setExecutionError(null);
    try {
      const selected = Array.from(selectedActions());
      const allPendingSelected = selected.length === pendingVisible().length;
      await props.onApprove(allPendingSelected ? undefined : selected);
      showSuccess("Proposal approved");
      setLastAction(null);
      // Do NOT close — let user see execution results
    } catch (err) {
      const message = mapProposalError(err);
      setExecutionError(message);
      showError(message);
    } finally {
      setIsExecuting(false);
    }
  };

  const handleReject = async () => {
    setLastAction(() => handleReject);
    setIsExecuting(true);
    setExecutionError(null);
    try {
      await props.onReject();
      showSuccess("Proposal rejected");
      props.onClose();
    } catch (err) {
      const message = mapProposalError(err);
      setExecutionError(message);
      showError(message);
    } finally {
      setIsExecuting(false);
    }
  };

  const handleRetry = () => {
    const action = lastAction();
    if (action) action();
  };

  const isExpired = createMemo(() => props.proposal?.status === "expired");

  const handleStartNewChat = () => {
    props.onClose();
    props.onStartNewChat?.();
  };

  return (
    <Modal
      open={!!props.proposal}
      onClose={props.onClose}
      title="Review Proposal"
      size="lg"
      closeOnBackdrop={!isExecuting()}
      closeOnEscape={!isExecuting()}
    >
      {/* Expired state */}
      <Show when={isExpired()}>
        <div class="flex flex-col items-center justify-center py-8 px-4 text-center">
          <div class="mb-4 text-signal-amber">
            <svg
              class="w-12 h-12 mx-auto"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="1.5"
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h3 class="text-lg font-medium text-ink mb-1">Proposal Expired</h3>
          <p class="text-sm text-ink-3 mb-6 max-w-sm">
            This proposal is no longer available. Proposals expire automatically
            after a period of inactivity. You can start a new conversation to
            request fresh changes.
          </p>
          <div class="flex gap-3">
            <Button variant="secondary" onClick={props.onClose}>
              Close
            </Button>
            <Show when={props.onStartNewChat}>
              <Button variant="primary" onClick={handleStartNewChat}>
                Start New Chat
              </Button>
            </Show>
          </div>
        </div>
      </Show>

      <Show when={!isExpired()}>
        <AiErrorBoundary
          surface="Proposal review"
          manualPath="You can close this dialog and change the items directly on the board instead."
          onDismiss={props.onClose}
        >
          <Show
            when={!props.loading && props.proposal}
            fallback={
              <div class="flex items-center justify-center py-8">
                <Spinner size="lg" />
                <span class="ml-2 text-ink-3">Loading proposal...</span>
              </div>
            }
          >
            {(proposal) => (
              <div class="space-y-4">
                {/* Status header */}
                <div class="flex items-center justify-between">
                  <Badge
                    variant={
                      proposalStatusBadge[proposal().status].variant as "todo"
                    }
                  >
                    {proposalStatusBadge[proposal().status].label}
                  </Badge>
                  <span class="text-sm text-ink-3">
                    {new Date(proposal().created_at).toLocaleString()}
                  </span>
                </div>

                {/* Impact summary */}
                <Show when={proposal().actions.length > 0}>
                  <ProposalImpactSummary actions={proposal().actions} />
                </Show>

                {/* AI Reasoning */}
                <Show when={proposal().reasoning}>
                  <Card
                    variant="bordered"
                    class="p-3 !bg-signal-magenta-tint !border-signal-magenta/25"
                  >
                    <div class="flex items-start gap-2">
                      <svg
                        class="w-5 h-5 text-signal-magenta shrink-0"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                        />
                      </svg>
                      <div>
                        <div class="text-sm font-medium text-signal-magenta">
                          AI Reasoning
                        </div>
                        <p class="text-sm text-ink-2 mt-1">
                          {proposal().reasoning}
                        </p>
                      </div>
                    </div>
                  </Card>
                </Show>

                {/* Selection controls */}
                <Show when={canTakeAction()}>
                  <div class="flex items-center justify-between border-b border-line pb-2">
                    <span class="text-sm font-medium text-ink-2">
                      Proposed Actions ({selectedCount()}/
                      {pendingVisible().length} selected)
                    </span>
                    <div class="flex gap-2">
                      <button
                        type="button"
                        class="text-sm text-ink-2 hover:text-ink font-medium"
                        onClick={selectAll}
                      >
                        Select All
                      </button>
                      <span class="text-ink-3">|</span>
                      <button
                        type="button"
                        class="text-sm text-ink-2 hover:text-ink"
                        onClick={deselectAll}
                      >
                        Deselect All
                      </button>
                    </div>
                  </div>
                </Show>

                {/* Actions grouped */}
                <div class="space-y-3 max-h-[400px] overflow-y-auto pr-1">
                  <For each={groups()}>
                    {(group) => (
                      <ActionGroupSection
                        group={group}
                        selectedActionIds={selectedActions()}
                        onToggleGroup={() => toggleGroup(group.id)}
                        onToggleAction={toggleAction}
                        canAct={canTakeAction()}
                      />
                    )}
                  </For>
                </div>

                {/* Partial approval warning */}
                <Show when={canTakeAction()}>
                  <PartialApprovalWarning
                    selectedCount={selectedCount()}
                    totalCount={pendingVisible().length}
                  />
                </Show>

                {/* Execution error */}
                <Show when={executionError()}>
                  <div class="bg-signal-red-tint border border-signal-red/25 rounded p-3">
                    <div class="flex items-center justify-between gap-2">
                      <div class="flex items-center gap-2 text-signal-red">
                        <svg
                          class="w-5 h-5 shrink-0"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        <span class="text-sm font-medium">
                          {executionError()}
                        </span>
                      </div>
                      <Show when={lastAction()}>
                        <button
                          type="button"
                          class="shrink-0 text-sm text-signal-red underline hover:text-ink"
                          onClick={handleRetry}
                          disabled={isExecuting()}
                        >
                          Retry
                        </button>
                      </Show>
                    </div>
                  </div>
                </Show>

                {/* Action buttons */}
                <div class="flex items-center justify-end gap-3 pt-2 border-t border-line">
                  <Show
                    when={canTakeAction()}
                    fallback={
                      <Button variant="secondary" onClick={props.onClose}>
                        Close
                      </Button>
                    }
                  >
                    {/* Reject via dropdown with confirmation */}
                    <Dropdown
                      align="left"
                      trigger={
                        <Button variant="secondary" disabled={isExecuting()}>
                          Reject
                        </Button>
                      }
                    >
                      <div class="px-3 py-2 text-sm text-ink-2 border-b border-line">
                        Reject this proposal? The AI's suggestions will be
                        discarded.
                      </div>
                      <DropdownItem
                        class="text-signal-red hover:bg-signal-red-tint focus:bg-signal-red-tint"
                        onClick={handleReject}
                      >
                        Confirm Reject
                      </DropdownItem>
                    </Dropdown>

                    <Button
                      variant="primary"
                      onClick={handleApprove}
                      disabled={!hasSelection() || isExecuting()}
                      loading={isExecuting()}
                    >
                      {selectedCount() === pendingVisible().length
                        ? "Approve All"
                        : `Approve Selected (${selectedCount()})`}
                    </Button>
                  </Show>
                </div>
              </div>
            )}
          </Show>
        </AiErrorBoundary>
      </Show>
    </Modal>
  );
};

export default ProposalReviewModal;
